import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import produce from 'immer';
import { first, isEmpty, toNumber, trim } from 'lodash';
import { subDays, subHours, subMinutes, subMonths, subWeeks, subYears } from 'date-fns';
import {
  formatDate,
  OnsetDateFormat,
  OnsetDateHourFormat,
  OnsetDateTimeFormat,
  OnsetMonthFormat,
  OnsetYearFormat,
  parseDateV2,
  SystemDateTimeSec,
} from '@cyren/common-lib';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import SurveyContainer from './SurveyContainer';
import LiveTestingContainer from '../../survey/shared/live-testing/LiveTestingContainer';
import LiveTestingQuestionField from '../../survey/shared/live-testing/LiveTestingQuestionField';
import { usePatientTransData } from '../../../hooks/use-patient-trans-data';
import { useTranslations } from '../../../../patient/hooks/use-translations';
import { useStaticLocales } from '../../../../admin/hooks/locales/use-static-locale';
import { usePatientLocale } from '../../../hooks/use-patient-locale';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import {
  getQuestionTreeControllerFindByTreeKeyQueryOptions,
} from '../../../api/generated/question-trees/question-trees';
import {
  getQuestionControllerFindByIdQueryOptions,
  getQuestionControllerFindByTreeIdQueryOptions,
} from '../../../api/generated/questions/questions';
import type {
  QuestionDetailResponseDto,
  QuestionResponseDto,
  ShowConditionDto,
  QuestionTreeResponseDto,
} from '../../../api/generated/types';
import { customInstance } from '../../../api/custom-instance';
import type {
  LiveTestingAnswerValue,
  LiveTestingAnswerMap,
  LiveTestingMultiTextNestedItem,
} from '../../survey/shared/live-testing/types';
import { conditionHelpers, matchAgeRange } from '../../../../report/helper-functions';
import { qsIncludesGlobal as reportQsIncludesGlobal } from '../../../../report/match-utils';
import classNames from 'classnames';
import PaTreeHeader from './components/PaTreeHeader';
import type { V2QsStage } from '../../../shared/components/question-types/V2QsStageMileMarker';
import { getTextAnswer } from '../../../../utils/models/question-answer';
import { useDelayedFlag } from './hooks/useDelayedFlag';
import {
  buildMergedSelectedSymptomKeys,
  collectSymptomItemAnswersFromTreeStates,
  collectRemovedSymptomTreeKeys,
  removeSymptomAnswerKeysFromAllTreesInPlace,
  removeSymptomTreeStatesInPlace,
} from './symptomSelectionSync';
import { getRemovalScopeSelectedSymptomTreeKeys } from './symptomRemovalScope';
import { splitInlineLabelSegments } from '../../../shared/utils/inlineLabelSegments';
import useConfirmationDialog from '../../../../hooks/use-confirmation-dialog';
import { useLocation } from 'react-router-dom';
import {
  buildSubmissionLockStorageKey,
  isIntegratedVisit,
  isSubmissionEditLocked,
} from './submissionLock';
import { normalizeQuestionLabelForSurveyLocale } from '../../../shared/utils/survey-locale-text';

const SAVE_DEBOUNCE_MS = 1500;
const TREE_LOADING_HINT_DELAY_MS = 350;
const PREFETCH_STALE_TIME_MS = 60 * 1000;

const SELECT_INPUT_TYPES = new Set([
  'select',
  'select_v2',
  'multiChoice',
  'radio',
  'select_sheet_single',
  'select_sheet_multi',
  'sx_select',
]);

const BODY_SELECT_TYPES = new Set([
  'body_select_belly',
  'body_select_chest',
  'body_select_back',
  'body_select_face',
  'body_select_limbs',
  'body_select_mouth',
  'body_select_dental',
  'body_select_sensory',
  'body_select_subpart',
  'body_select_head',
  'body_select_arm',
  'body_select_leg',
  'body_select_joints',
]);

function filterEligibleConditions({
  treeKey,
  showConditions,
}: {
  treeKey?: string;
  showConditions?: ShowConditionDto[] | null;
}) {
  if (!treeKey) {
    return showConditions || [];
  }

  const showConditionTreeFiltered = showConditions?.filter((condition) => {
    const { targetTreeKeys, targetTreeKey } = condition;

    if (!isEmpty(targetTreeKeys) && !targetTreeKeys?.includes(treeKey || '')) {
      return false;
    }

    if (targetTreeKey && targetTreeKey !== treeKey) {
      return false;
    }

    return true;
  });

  return showConditionTreeFiltered || [];
}

function normalizeInputType(question: QuestionResponseDto) {
  const rawInputType = question.inputType;
  if (rawInputType === 'select_sheet') {
    const isMulti = (question.typeOptions as { multi?: boolean })?.multi === true;
    return isMulti ? 'select_sheet_multi' : 'select_sheet_single';
  }
  return rawInputType;
}

function getStepNameKey(tree: { stepNameKey?: unknown; treeKey?: string } | undefined) {
  if (!tree) return '';
  const raw = tree.stepNameKey as unknown;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && 'key' in raw) {
    const value = (raw as { key?: string }).key;
    if (typeof value === 'string') return value;
  }
  return `${tree.treeKey}-name-key`;
}

function isEmptyAnswerValue(value: LiveTestingAnswerValue) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return trim(value).length === 0;
  if (typeof value === 'number') return false;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    if ('selectedKeys' in value) {
      return (value.selectedKeys as string[]).length === 0;
    }
    if ('selectedKeyGroups' in value) {
      return (value.selectedKeyGroups as string[][]).length === 0;
    }
    return Object.keys(value).length === 0;
  }
  return true;
}

function buildRelativeOnsetAnswer(
  value: { duration?: number; unit?: string } | undefined,
  baseDateStr?: string
) {
  if (!value?.duration || !value?.unit) return null;

  const { duration, unit } = value;
  const resolvedBaseDateStr = baseDateStr || formatDate(new Date(), { formatStr: SystemDateTimeSec });
  const baseDate = parseDateV2({ dateStr: resolvedBaseDateStr, formatStr: SystemDateTimeSec }) || new Date();
  let date = baseDate;
  let formatStr = OnsetDateFormat;

  const unitHandlers: Record<
    string,
    {
      calc: (date: Date, amount: number) => Date;
      format: string;
    }
  > = {
    minute: { calc: subMinutes, format: OnsetDateTimeFormat },
    hour: { calc: subHours, format: OnsetDateHourFormat },
    day: { calc: subDays, format: OnsetDateFormat },
    week: { calc: subWeeks, format: OnsetDateFormat },
    month: { calc: subMonths, format: OnsetMonthFormat },
    year: { calc: subYears, format: OnsetYearFormat },
  };

  const handler = unitHandlers[unit];
  if (handler) {
    date = handler.calc(baseDate, duration);
    formatStr = handler.format;
  }

  return {
    answerValues: [formatDate(date, { formatStr })],
    uiMetadata: {
      duration: `${duration}`,
      unit,
      baseDateStr: resolvedBaseDateStr,
    },
  };
}

function renderInlineLabel(text: string) {
  const segments = splitInlineLabelSegments(text);
  if (segments.length === 0) return text;

  return segments.map((segment, index) => {
    if (segment.bold) {
      return (
        <strong key={`bold-${index}`}>
          {segment.text}
        </strong>
      );
    }
    return (
      <Fragment key={`text-${index}`}>
        {segment.text}
      </Fragment>
    );
  });
}

function PaSurveyTreePageBody({
  treeTitleKey,
  treeDescKey,
  stageTranslationKeys,
}: {
  treeTitleKey: string;
  treeDescKey: string;
  stageTranslationKeys: string[];
}) {
  const {
    reportState,
    survey,
    visit,
    setReportState,
    navigation,
    updateVisit,
    isLastTree,
  } = SurveyContainer.useContainer();
  const { treeKey } = navigation.params;
  const { localeKey: patientLocaleKey, localeCode } = usePatientLocale();
  const [, { t }] = useTranslations({ locale: patientLocaleKey });
  const { sltStr } = useStaticLocales({ localeCode });
  const [confirmDialog, confirmationDialogUX] = useConfirmationDialog();

  const { allQuestions, isLoading, selectedTreeId, selectedSymptomTreeKeys } = LiveTestingContainer.useContainer();
  const selectedSxKeys = useMemo(
    () => (selectedSymptomTreeKeys || []).filter((key) => key && key.startsWith('sx-')),
    [selectedSymptomTreeKeys]
  );
  const questionTreeKeys = survey?.questionTreeKeys || [];
  const currentIndex = treeKey ? questionTreeKeys.indexOf(treeKey) : -1;
  const [openDesc, setOpenDesc] = useState<{ key: string; text: string } | null>(null);

  const conditionQuestionIds = useMemo(
    () => allQuestions.filter((question) => question.hasConditions).map((question) => question.id),
    [allQuestions]
  );

  const questionDetailQueries = useQueries({
    queries: conditionQuestionIds.map((id) => ({
      ...getQuestionControllerFindByIdQueryOptions(id),
      enabled: !!id,
    })),
  });

  const questionDetailsById = useMemo(() => {
    const map = new Map<number, QuestionDetailResponseDto>();
    conditionQuestionIds.forEach((id, idx) => {
      const data = questionDetailQueries[idx]?.data as QuestionDetailResponseDto | undefined;
      if (data) {
        map.set(id, data);
      }
    });
    return map;
  }, [conditionQuestionIds, questionDetailQueries]);

  const translationKeys = useMemo(() => {
    const keys = new Set<string>();
    if (treeTitleKey) keys.add(treeTitleKey);
    if (treeDescKey) keys.add(treeDescKey);
    stageTranslationKeys.forEach((key) => keys.add(key));
    allQuestions.forEach((question) => {
      if (question.questionKey) keys.add(question.questionKey);
      const detail = questionDetailsById.get(question.id);
      if (detail?.desc) keys.add(detail.desc);
      if (detail?.descInline) keys.add(detail.descInline);
      if (detail?.exampleKey) keys.add(detail.exampleKey);
      if (detail?.thirdPartyDesc) keys.add(detail.thirdPartyDesc);
      if (detail?.thirdPartyDescInline) keys.add(detail.thirdPartyDescInline);
      if (detail?.thirdPartyExample) keys.add(detail.thirdPartyExample);
      question.answerKeys?.forEach((key) => {
        if (key) keys.add(key);
      });
    });
    return Array.from(keys);
  }, [allQuestions, questionDetailsById, treeTitleKey, stageTranslationKeys]);

  const [{ loadingTrans }] = usePatientTransData({
    keys: translationKeys,
    locale: patientLocaleKey as any,
  });

  const treeState = useMemo(() => {
    return reportState.treeStates.find((ts) => ts.treeKey === treeKey);
  }, [reportState.treeStates, treeKey]);

  const selectedSymptomItemAnswers = useMemo(
    () =>
      collectSymptomItemAnswersFromTreeStates({
        treeStates: reportState.treeStates || [],
        selectedSymptomTreeKeys: selectedSxKeys,
      }),
    [reportState.treeStates, selectedSxKeys]
  );

  useEffect(() => {
    if (!treeKey || treeState) return;
    setReportState((prev) =>
      produce(prev, (draft) => {
        draft.treeStates.push({
          treeKey,
          answerMap: {},
        });
      })
    );
  }, [setReportState, treeKey, treeState]);

  const dirtyRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedKeyRef = useRef<string | null>(null);

  const scheduleSave = useCallback(() => {
    if (!visit?.id) return;
    if (!dirtyRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const snapshot = JSON.stringify(reportState);
      if (lastSavedKeyRef.current === snapshot) {
        dirtyRef.current = false;
        return;
      }
      updateVisit(reportState);
      lastSavedKeyRef.current = snapshot;
      dirtyRef.current = false;
    }, SAVE_DEBOUNCE_MS);
  }, [reportState, updateVisit, visit?.id]);

  useEffect(() => {
    scheduleSave();
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [scheduleSave]);

  const isQuestionRequired = useCallback(
    (question: QuestionResponseDto) => {
      const detail = questionDetailsById.get(question.id);
      if (detail?.required != null) return detail.required;
      if (question.optional != null) return !question.optional;
      return false;
    },
    [questionDetailsById]
  );

  const isQuestionVisible = useCallback(
    (question: QuestionResponseDto) => {
      if (!treeState) return true;
      if (!question.enabled) return false;

      const detail = questionDetailsById.get(question.id);
      const showConditions =
        ((question as any).showConditions as ShowConditionDto[] | undefined | null) ||
        (detail?.showConditions as ShowConditionDto[] | undefined | null);

      if (!showConditions || showConditions.length === 0) {
        if (question.hasConditions) {
          return false;
        }
        return true;
      }

      const eligibleConditions = filterEligibleConditions({
        treeKey,
        showConditions,
      });

      if (isEmpty(eligibleConditions)) {
        return true;
      }

      const evaluateCondition = (condition: ShowConditionDto) => {
        const { operator, qsKey, value, func, category, ageMin, ageMax, ageUnit, type } = condition;

        if (operator != null) {
          const firstValue = first(treeState.answerMap?.[qsKey || '']?.answerValues);
          if (firstValue != null) {
            const lhs = toNumber(firstValue);
            const rhs = toNumber(value);
            if (operator === 'lte' && lhs <= rhs) return true;
            if (operator === 'lt' && lhs < rhs) return true;
            if (operator === 'gte' && lhs >= rhs) return true;
            if (operator === 'gt' && lhs > rhs) return true;
            if (operator === 'eq' && lhs === rhs) return true;
            if (operator === 'ne' && lhs !== rhs) return true;
          }
        }

        if (func) {
          const helper = conditionHelpers[func as keyof typeof conditionHelpers];
          if (helper) {
            return helper({
              question: question as any,
              treeState: treeState as any,
              reportState: reportState as any,
              survey: survey as any,
              condition: condition as any,
            });
          }
        }

        if (category === 'age-range') {
          return matchAgeRange({
            reportState: reportState as any,
            ageMin,
            ageMax,
            ageUnit: ageUnit as any,
          });
        }

        if (type === 'include') {
          return reportQsIncludesGlobal({
            condition: condition as any,
            question: question as any,
            treeState: treeState as any,
            treeStates: reportState.treeStates as any,
          });
        }

        return false;
      };

      const andConditions = eligibleConditions.filter((cond) => cond.logicType === 'AND');
      const orConditions = eligibleConditions.filter((cond) => cond.logicType === 'OR' || !cond.logicType);

      const andMet = andConditions.length === 0 || andConditions.every((condition) => evaluateCondition(condition));
      const orMet = orConditions.length === 0 || orConditions.some((condition) => evaluateCondition(condition));

      return andMet && orMet;
    },
    [questionDetailsById, reportState, survey, treeKey, treeState]
  );

  const visibleQuestions = useMemo(
    () => allQuestions.filter((question) => isQuestionVisible(question)),
    [allQuestions, isQuestionVisible]
  );

  const getAnswerValue = useCallback(
    (question: QuestionResponseDto): LiveTestingAnswerValue => {
      const answerState = treeState?.answerMap?.[question.questionKey];
      const inputType = normalizeInputType(question);

      if (!answerState) {
        if (SELECT_INPUT_TYPES.has(inputType)) {
          return { selectedKeys: [], notesForKeys: {} };
        }
        if (inputType === 'body_select' || inputType === 'body_select_whole') {
          return { selectedKeyGroups: [] };
        }
        if (BODY_SELECT_TYPES.has(inputType)) {
          return { answerKeys: [] };
        }
        if (inputType === 'sx_collect' || inputType === 'sx_collect_intelligent' || inputType === 'sx_collect_associated') {
          return {
            selectedKeys: selectedSxKeys,
            itemAnswers: selectedSymptomItemAnswers,
          };
        }
        if (inputType === 'multi_text' || inputType === 'multi_text_nested') {
          return [];
        }
        return '';
      }

      if (SELECT_INPUT_TYPES.has(inputType)) {
        return {
          selectedKeys: answerState.answerKeys || [],
          notesForKeys: answerState.notesForKeys || {},
        };
      }

      if (inputType === 'body_select' || inputType === 'body_select_whole') {
        return {
          selectedKeyGroups: answerState.selectedKeyGroups || [],
          notesForKeyGroups: answerState.notesForKeyGroups || [],
          answerMaps: answerState.answerMaps || [],
        };
      }

      if (BODY_SELECT_TYPES.has(inputType)) {
        return {
          answerKeys: answerState.answerKeys || [],
        };
      }

      if (inputType === 'sx_collect' || inputType === 'sx_collect_intelligent' || inputType === 'sx_collect_associated') {
        const mergedSelectedKeys = buildMergedSelectedSymptomKeys({
          answerKeys: answerState.answerKeys || [],
          selectedSymptomTreeKeys: selectedSxKeys,
        });
        const answerStateItemAnswers = (answerState as any).itemAnswers || {};
        return {
          selectedKeys: mergedSelectedKeys,
          itemAnswers: {
            ...selectedSymptomItemAnswers,
            ...answerStateItemAnswers,
          },
          uiMetadata: (answerState as any).uiMetadata || {},
        };
      }

      if (inputType === 'multi_text') {
        const rawValue = String(answerState.answerValues?.[0] ?? '');
        return rawValue ? rawValue.split('|').filter((val) => trim(val).length > 0) : [];
      }

      if (inputType === 'multi_text_nested') {
        const rawValue = String(answerState.answerValues?.[0] ?? '');
        const texts = rawValue ? rawValue.split('|') : [];
        const answerMaps = (answerState.answerMaps || []) as Array<LiveTestingAnswerMap>;
        const maxLen = Math.max(texts.length, answerMaps.length);

        return Array.from({ length: maxLen }).map<LiveTestingMultiTextNestedItem>((_, idx) => {
          const map = answerMaps[idx];
          return {
            id: `${question.questionKey}-${idx}`,
            text: texts[idx] || '',
            selectedOptions: (map?.answerKeys as string[]) || [],
            notesForKeys: (map?.notesForKeys as Record<string, string>) || {},
          };
        });
      }

      if (inputType === 'select_countries') {
        const values = answerState.answerValues || [];
        return values as string[];
      }

      if (inputType === 'blood_pressure') {
        const [systolic, diastolic] = answerState.answerValues || [];
        return {
          systolic: typeof systolic === 'number' ? systolic : Number(systolic || 0),
          diastolic: typeof diastolic === 'number' ? diastolic : Number(diastolic || 0),
        };
      }

      if (inputType === 'frequency') {
        const [times, period] = answerState.answerValues || [];
        return {
          times: typeof times === 'number' ? times : Number(times || 0),
          period: typeof period === 'string' ? period : `${period || ''}`,
        };
      }

      if (inputType === 'relative_onset' || inputType === 'datetime_relative_onset') {
        const duration = answerState.uiMetadata?.duration;
        const unit = answerState.uiMetadata?.unit;
        return {
          duration: duration ? Number(duration) : undefined,
          unit: unit as string | undefined,
        };
      }

      if (inputType === 'range' || inputType === 'number') {
        const value = answerState.answerValues?.[0];
        return typeof value === 'number' ? value : Number(value ?? 0);
      }

      if (inputType === 'image' || inputType === 'draw_body') {
        const value = answerState.answerValues?.[0];
        if (inputType === 'draw_body') {
          return { imageData: typeof value === 'string' ? value : '' };
        }
        return typeof value === 'string' ? value : '';
      }

      const firstValue = answerState.answerValues?.[0];
      if (typeof firstValue === 'string' || typeof firstValue === 'number') {
        return firstValue;
      }
      return '';
    },
    [selectedSxKeys, selectedSymptomItemAnswers, treeState?.answerMap]
  );

  const handleAnswerChange = useCallback(
    (question: QuestionResponseDto, value: LiveTestingAnswerValue) => {
      if (!treeKey) return;

      const inputType = normalizeInputType(question);
      const systemKey = (question as QuestionResponseDto & { systemKey?: string | null }).systemKey || undefined;
      const isSxCollectorInputType =
        inputType === 'sx_collect' ||
        inputType === 'sx_collect_intelligent' ||
        inputType === 'sx_collect_associated';
      const previousCollectorAnswerKeys = isSxCollectorInputType
        ? ((treeState?.answerMap?.[question.questionKey]?.answerKeys as string[] | undefined) || [])
        : [];
      const nextCollectorSelectedKeys = isSxCollectorInputType
        ? (((value as { selectedKeys?: string[] } | undefined)?.selectedKeys || []) as string[])
        : [];
      const removedSymptomTreeKeys = isSxCollectorInputType
        ? collectRemovedSymptomTreeKeys({
          previousAnswerKeys: previousCollectorAnswerKeys,
          selectedSymptomTreeKeys: getRemovalScopeSelectedSymptomTreeKeys(
            inputType,
            selectedSxKeys,
          ),
          nextSelectedKeys: nextCollectorSelectedKeys,
        })
        : [];

      if (isEmptyAnswerValue(value)) {
        dirtyRef.current = true;
        setReportState((prev) =>
          produce(prev, (draft) => {
            const targetTree = draft.treeStates.find((ts) => ts.treeKey === treeKey);
            if (!targetTree?.answerMap) return;
            delete targetTree.answerMap[question.questionKey];
            if (systemKey) {
              if (!draft.systemValueMap) draft.systemValueMap = {};
              draft.systemValueMap[systemKey] = '';
              if (systemKey === 'chiefComplaint') {
                draft.systemValueMap.cc = '';
              }
            }
            if (question.questionKey === 'q-chief-complaint-text') {
              if (!draft.systemValueMap) draft.systemValueMap = {};
              draft.systemValueMap.cc = '';
            }
            removeSymptomAnswerKeysFromAllTreesInPlace(draft.treeStates, removedSymptomTreeKeys);
            removeSymptomTreeStatesInPlace(draft.treeStates, removedSymptomTreeKeys);
          })
        );
        return;
      }

      const nextAnswerState = (() => {
        if (SELECT_INPUT_TYPES.has(inputType)) {
          const selectedKeys = 'selectedKeys' in (value as any) ? (value as any).selectedKeys : [];
          const notesForKeys = 'notesForKeys' in (value as any) ? (value as any).notesForKeys : {};
          return {
            answerKeys: selectedKeys,
            notesForKeys,
          };
        }

        if (inputType === 'body_select' || inputType === 'body_select_whole') {
          return {
            selectedKeyGroups: (value as any).selectedKeyGroups || [],
            notesForKeyGroups: (value as any).notesForKeyGroups || [],
            answerMaps: (value as any).answerMaps || [],
          };
        }

        if (BODY_SELECT_TYPES.has(inputType)) {
          return {
            answerKeys: (value as any).answerKeys || [],
          };
        }

        if (inputType === 'sx_collect' || inputType === 'sx_collect_intelligent' || inputType === 'sx_collect_associated') {
          const collectorValue = (value as any) || {};
          const selectedKeys = collectorValue.selectedKeys || [];
          const itemAnswers = collectorValue.itemAnswers || {};
          const uiMetadata = collectorValue.uiMetadata || {};
          const hasSelectedSymptoms = selectedKeys.some((key: string) => key && key.startsWith('sx-'));

          return {
            answerKeys: selectedKeys,
            itemAnswers,
            uiMetadata: {
              ...uiMetadata,
              hasSelectedSymptoms: hasSelectedSymptoms ? 'true' : 'false',
              // If no symptom tree is selected (e.g. only "none above"), associated symptoms must be false.
              hasAssociatedSymptoms:
                hasSelectedSymptoms && uiMetadata.hasAssociatedSymptoms === 'true' ? 'true' : 'false',
            },
          };
        }

        if (inputType === 'multi_text') {
          const list = Array.isArray(value) ? value : [];
          const joined = list.map((item) => trim(String(item))).filter(Boolean).join('|');
          return {
            answerValues: joined ? [joined] : [],
          };
        }

        if (inputType === 'multi_text_nested') {
          const tags = Array.isArray(value) ? value : [];
          const texts = tags.map((tag: any) => trim(String(tag.text || ''))).filter(Boolean);
          const answerMaps = tags.map((tag: any) => ({
            answerKeys: tag.selectedOptions || [],
            notesForKeys: tag.notesForKeys || {},
          }));
          return {
            answerValues: texts.length > 0 ? [texts.join('|')] : [],
            answerMaps,
          };
        }

        if (inputType === 'select_countries') {
          const values = Array.isArray(value) ? value : [];
          return {
            answerValues: values,
          };
        }

        if (inputType === 'blood_pressure') {
          return {
            answerValues: [(value as any).systolic ?? '', (value as any).diastolic ?? ''],
          };
        }

        if (inputType === 'frequency') {
          return {
            answerValues: [(value as any).times ?? '', (value as any).period ?? ''],
          };
        }

        if (inputType === 'relative_onset' || inputType === 'datetime_relative_onset') {
          const existingBaseDateStr = treeState?.answerMap?.[question.questionKey]?.uiMetadata?.baseDateStr as
            | string
            | undefined;
          const relative = buildRelativeOnsetAnswer(
            value as { duration?: number; unit?: string },
            existingBaseDateStr
          );
          return relative || {};
        }

        if (inputType === 'range' || inputType === 'number') {
          return {
            answerValues: [value as number],
          };
        }

        if (inputType === 'image') {
          return {
            answerValues: [value as string],
          };
        }

        if (inputType === 'draw_body') {
          return {
            answerValues: [(value as any).imageData || ''],
          };
        }

        return {
          answerValues: [value as string],
        };
      })();

      dirtyRef.current = true;
      setReportState((prev) =>
        produce(prev, (draft) => {
          const targetTree =
            draft.treeStates.find((ts) => ts.treeKey === treeKey) ||
            (() => {
              const newTree = { treeKey, answerMap: {} as Record<string, any> };
              draft.treeStates.push(newTree);
              return newTree;
            })();

          if (!targetTree.answerMap) {
            targetTree.answerMap = {};
          }
          targetTree.answerMap[question.questionKey] = nextAnswerState;
          if (systemKey) {
            if (!draft.systemValueMap) draft.systemValueMap = {};
            const systemValue = trim(getTextAnswer(nextAnswerState as any) || '');
            draft.systemValueMap[systemKey] = systemValue;
            if (systemKey === 'chiefComplaint') {
              draft.systemValueMap.cc = systemValue;
            }
          }
          if (question.questionKey === 'q-chief-complaint-text') {
            if (!draft.systemValueMap) draft.systemValueMap = {};
            const systemValue = trim(getTextAnswer(nextAnswerState as any) || '');
            draft.systemValueMap.cc = systemValue;
          }
          removeSymptomAnswerKeysFromAllTreesInPlace(draft.treeStates, removedSymptomTreeKeys);
          removeSymptomTreeStatesInPlace(draft.treeStates, removedSymptomTreeKeys);
        })
      );
    },
    [selectedSxKeys, setReportState, treeKey, treeState?.answerMap]
  );

  const hasMissingRequired = useMemo(() => {
    if (!treeState?.answerMap) return false;
    return visibleQuestions.some((question) => {
      if (!isQuestionRequired(question)) return false;
      const value = getAnswerValue(question);
      return isEmptyAnswerValue(value);
    });
  }, [getAnswerValue, isQuestionRequired, treeState?.answerMap, visibleQuestions]);

  const localizedQuestions = useMemo(() => {
    return visibleQuestions.map((question) => {
      const detail = questionDetailsById.get(question.id);
      const translatedLabel = t(question.questionKey, null, {
        fallbackValue: question.label || '',
      });
      const label = normalizeQuestionLabelForSurveyLocale({
        locale: patientLocaleKey,
        label: translatedLabel,
      });
      const descriptionKey = detail?.desc || question.description;
      const descriptionInlineKey = detail?.descInline || question.descriptionInline;
      const exampleKey = detail?.exampleKey || question.exampleKey || question.example;
      const description = descriptionKey
        ? t(descriptionKey, null, { fallbackValue: question.description || (loadingTrans ? '' : descriptionKey) })
        : undefined;
      const descriptionInline = descriptionInlineKey
        ? t(descriptionInlineKey, null, { fallbackValue: question.descriptionInline || (loadingTrans ? '' : descriptionInlineKey) })
        : undefined;
      const exampleTranslated = exampleKey
        ? t(exampleKey, null, { fallbackValue: null })
        : null;
      const example = exampleTranslated
        || (question.example && question.example !== question.exampleKey ? question.example : undefined);

      const answerOptionsMap = question.answerOptionsMap
        ? Object.fromEntries(
          Object.entries(question.answerOptionsMap).map(([key, option]) => [
            key,
            {
              ...option,
              label: t(key, null, {
                fallbackValue: (option as { label?: string }).label || (loadingTrans ? '' : key),
              }),
            },
          ])
        )
        : question.answerOptionsMap;

      return {
        ...question,
        label,
        description,
        descriptionInline,
        example,
        answerOptionsMap,
      };
    });
  }, [visibleQuestions, questionDetailsById, t, loadingTrans]);

  const treeTitle = treeTitleKey
    ? t(treeTitleKey, null, { fallbackValue: loadingTrans ? '' : treeTitleKey }) || ''
    : '';
  const treeDesc = treeDescKey
    ? t(treeDescKey, null, { fallbackValue: '' }) || ''
    : '';

  const hasStageMileMarker = useMemo(
    () => localizedQuestions.some((question) => normalizeInputType(question) === 'stage_milemarker'),
    [localizedQuestions]
  );
  const isBlockingTreeLoading = isLoading || (!selectedTreeId && Boolean(treeKey));
  const showBlockingTreeLoading = useDelayedFlag(isBlockingTreeLoading, TREE_LOADING_HINT_DELAY_MS);

  if (showBlockingTreeLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-[#737580]">
        {sltStr({ key: 'survey-question-loading' })}
      </div>
    );
  }
  if (isBlockingTreeLoading) {
    return <div className="flex-1" aria-hidden />;
  }

  if (!treeKey || visibleQuestions.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-[#737580]">
        {sltStr({ key: 'survey-steps-no-questions' })}
      </div>
    );
  }

  if (loadingTrans) {
    return (
      <div className="t_PaQuestionTree flex-1 flex-col gap-2 overflow-hidden w-full">
        <div className="flex-1 overflow-auto px-5 flex-col gap-8 w-full animate-pulse">
          <div className="flex-col gap-2 items-center text-center">
            <div className="h-6 w-48 bg-gray-200 rounded mx-auto" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="gap-4 flex-col rounded-xl">
              <div className="h-5 w-3/4 bg-gray-200 rounded" />
              <div className="h-3 w-1/3 bg-gray-100 rounded" />
              <div className="h-12 w-full bg-gray-100 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="t_PaQuestionTree flex-1 flex-col gap-2 overflow-hidden w-full">
      <div className="flex-1 overflow-auto px-5 flex-col gap-8 w-full">
        {(treeTitle || treeDesc) && !hasStageMileMarker && (
          <div className="flex-col gap-2 items-center text-center">
            {treeTitle && <div className="text-lg font-bold text-primary">{treeTitle}</div>}
            {treeDesc && <div className="text-desc">{treeDesc}</div>}
          </div>
        )}
        {localizedQuestions.map((question) => {
          const inputType = normalizeInputType(question);
          const hideHeader = inputType === 'stage_milemarker' || inputType === 'eq_vas';
          return (
            <div key={question.questionKey} className="t_Question gap-4 flex-col rounded-xl">
              {!hideHeader && (
                <div className="flex-col">
                  <div className="flex gap-2 text-base items-center">
                    <span>
                      {renderInlineLabel(question.label || '')}
                      {isQuestionRequired(question) && <span title="Required"> *</span>}
                    </span>
                    {question.description && (
                      <button
                        type="button"
                        className="opacity-50 hover:opacity-70 transition-opacity flex items-center justify-center"
                        onClick={() =>
                          setOpenDesc({
                            key: question.questionKey,
                            text: question.description || '',
                          })
                        }
                        aria-label="Show description"
                      >
                        <FaRegQuestionCircle size={18} />
                      </button>
                    )}
                  </div>
                  {question.descriptionInline && (
                    <div className="text-desc whitespace-pre-wrap">{question.descriptionInline}</div>
                  )}
                  {!isQuestionRequired(question) && (
                    <span className="text-desc">({sltStr({ key: 'optional' })})</span>
                  )}
                  {question.example && (
                    <div className="pt-1 text-desc whitespace-pre-wrap">{sltStr({ key: 'label-example' })}: {question.example}</div>
                  )}
                </div>
              )}

              <LiveTestingQuestionField
                question={question}
                value={getAnswerValue(question)}
                onChange={(value) => handleAnswerChange(question, value)}
                chiefComplaint={String(
                  reportState.systemValueMap?.cc || reportState.systemValueMap?.chiefComplaint || ''
                )}
              />
            </div>
          );
        })}
      </div>

      {openDesc?.text && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50"
          onClick={() => setOpenDesc(null)}
        >
          <div
            className="w-full max-w-lg bg-white rounded-t-3xl lg:rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3">
              <div className="bg-black bg-opacity-10 rounded-full w-10 h-2" />
            </div>
            <div className="flex justify-end px-5 pt-2">
              <button
                className="p-2 hover:opacity-70 transition-opacity"
                onClick={() => setOpenDesc(null)}
                aria-label="Close"
              >
                <MdClose size={20} className="opacity-60" />
              </button>
            </div>
            <div className="px-5 pb-6 whitespace-pre-wrap text-base text-[#222638]">
              {openDesc.text}
            </div>
          </div>
        </div>
      )}

      <div className="px-5 w-full">
        <div className="flex w-full">
          <button
            type="button"
            className={classNames('btn btn-primary flex-1 btn-lg', hasMissingRequired && 'btn-disabled')}
            disabled={hasMissingRequired}
            onClick={async () => {
              if (isLastTree) {
                const yes = await confirmDialog(
                  '',
                  sltStr({ key: 'msg-confirm-save' }),
                  sltStr({ key: 'btn-cancel' }),
                  sltStr({ key: 'btn-submit' }),
                );
                if (!yes) return;
                navigation.onSave();
              } else {
                navigation.goNextTree();
              }
            }}
          >
            {isLastTree
              ? sltStr({ key: 'btn-submit', title: true })
              : sltStr({ key: 'btn-next', capit: true })}
          </button>
        </div>
        {confirmationDialogUX}
        <div className="h-10" />
      </div>
    </div>
  );
}

function PaSurveyTreePageContent() {
  const location = useLocation();
  const { survey, org, navigation, reportState, visit, treeByKey, treeSearchResults } =
    SurveyContainer.useContainer();
  const queryClient = useQueryClient();
  const { localeKey: patientLocaleKey } = usePatientLocale();
  const [, { t }] = useTranslations({ locale: patientLocaleKey });
  const { treeKey, shortId } = navigation.params;
  const surveyId = survey?.id;
  const organizationId = survey?.organization?.id;
  const treeKeys = survey?.questionTreeKeys || [];
  const treeKeysKey = useMemo(() => treeKeys.join('|'), [treeKeys]);
  const [stageTreeDetails, setStageTreeDetails] = useState<QuestionTreeResponseDto[]>([]);
  const searchResults = treeSearchResults || [];

  const treeId = useMemo(() => {
    if (treeByKey?.id) return treeByKey.id;
    if (!treeKey || !searchResults) return null;
    return searchResults.find((tree) => tree.treeKey === treeKey)?.id || null;
  }, [treeByKey?.id, searchResults, treeKey]);

  const treeTitleKey = useMemo(() => {
    const fromDetails = stageTreeDetails.find((tree) => tree.treeKey === treeKey);
    const detailKey = getStepNameKey(fromDetails);
    if (detailKey) return detailKey;
    const stepNameKey = searchResults?.find((tree) => tree.treeKey === treeKey)?.stepNameKey;
    if (typeof stepNameKey === 'string') return stepNameKey;
    if (stepNameKey && typeof stepNameKey === 'object' && 'key' in stepNameKey) {
      const value = (stepNameKey as { key?: string }).key;
      if (typeof value === 'string') return value;
    }
    if (!treeKey) return '';
    return `${treeKey}-name-key`;
  }, [searchResults, stageTreeDetails, treeKey]);

  const treeDescKey = useMemo(() => {
    return treeTitleKey ? `${treeTitleKey}-desc-key` : '';
  }, [treeTitleKey]);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const aid = searchParams.get('aid');
  const eid = searchParams.get('eid');
  const referralMessageId = navigation.params.referralMessageId || searchParams.get('referralMessageId');
  const isIntegrated = isIntegratedVisit({ aid, eid, referralMessageId });
  const submissionLockStorageKey = useMemo(
    () =>
      buildSubmissionLockStorageKey({
        shortId,
        aid,
        eid,
        referralMessageId,
      }),
    [shortId, aid, eid, referralMessageId],
  );
  const localSubmittedAt = useMemo(() => {
    try {
      return localStorage.getItem(submissionLockStorageKey);
    } catch (error) {
      console.warn('Failed to read submission lock timestamp:', error);
      return null;
    }
  }, [submissionLockStorageKey]);

  const isSubmissionLocked = useMemo(
    () =>
      isSubmissionEditLocked({
        visit,
        localSubmittedAt,
        isIntegrated,
      }),
    [visit, localSubmittedAt, isIntegrated],
  );

  useEffect(() => {
    if (!treeKey || !isSubmissionLocked) return;
    navigation.navigate(navigation.finishedUrl);
  }, [treeKey, isSubmissionLocked, navigation]);

  useEffect(() => {
    let isActive = true;
    const fetchStageTrees = async () => {
      if (!surveyId || treeKeys.length === 0) {
        if (isActive) {
          setStageTreeDetails([]);
        }
        return;
      }

      try {
        const result = await customInstance<QuestionTreeResponseDto[]>({
          method: 'POST',
          url: '/question-trees/find',
          data: {
            surveyId: Number(surveyId),
            treeKeys,
          },
        });
        if (isActive) {
          setStageTreeDetails(result || []);
        }
      } catch (error) {
        console.warn('Failed to load stage trees:', error);
        if (isActive) {
          setStageTreeDetails([]);
        }
      }
    };

    fetchStageTrees();
    return () => {
      isActive = false;
    };
  }, [surveyId, treeKeysKey]);

  const stageTrees = useMemo(() => {
    if (!treeKeys.length || stageTreeDetails.length === 0) return [];
    const byKey = new Map(stageTreeDetails.map((tree) => [tree.treeKey, tree]));
    return treeKeys
      .map((key) => byKey.get(key))
      .filter(
        (tree): tree is QuestionTreeResponseDto =>
          Boolean(tree?.stageBoundary) && tree?.enabled !== false
      );
  }, [stageTreeDetails, treeKeys]);

  const enabledStepKeys = useMemo(() => {
    if (!treeKeys.length) return [];
    if (!stageTreeDetails.length) return treeKeys;
    const byKey = new Map(stageTreeDetails.map((tree) => [tree.treeKey, tree]));
    return treeKeys.filter((key) => {
      const tree = byKey.get(key);
      return tree ? tree.enabled !== false : true;
    });
  }, [stageTreeDetails, treeKeys]);
  const nextTreeKeyForPrefetch = useMemo(() => {
    if (!treeKey) return null;
    const sourceKeys = enabledStepKeys.length > 0 ? enabledStepKeys : treeKeys;
    if (!sourceKeys.length) return null;
    const currentStepIndex = sourceKeys.indexOf(treeKey);
    if (currentStepIndex < 0) return null;
    return sourceKeys[currentStepIndex + 1] || null;
  }, [enabledStepKeys, treeKey, treeKeys]);

  const stageTranslationKeys = useMemo(() => {
    return stageTrees.flatMap((tree) => {
      const nameKey = getStepNameKey(tree);
      return nameKey ? [nameKey, `${nameKey}-desc-key`] : [];
    });
  }, [stageTrees]);

  const stageMarkerStages = useMemo<V2QsStage[]>(() => {
    let foundCurrent = false;
    return stageTrees.map((tree) => {
      const isCurrent = tree.treeKey === treeKey;
      const nameKey = getStepNameKey(tree);
      const descKey = nameKey ? `${nameKey}-desc-key` : '';
      const name =
        nameKey ? t(nameKey, null, { fallbackValue: '' }) || '' : '';
      const description = descKey
        ? t(descKey, null, { fallbackValue: '' }) || ''
        : '';

      let status: V2QsStage['status'] = 'pending';
      if (isCurrent) {
        status = 'current';
        foundCurrent = true;
      } else if (!foundCurrent) {
        status = 'completed';
      }

      return {
        key: tree.treeKey,
        name,
        description,
        status,
      };
    });
  }, [stageTrees, t, treeKey]);

  useEffect(() => {
    let cancelled = false;
    if (!surveyId || !nextTreeKeyForPrefetch) {
      return () => {
        cancelled = true;
      };
    }
    const parsedSurveyId = Number(surveyId);
    if (!Number.isFinite(parsedSurveyId) || parsedSurveyId <= 0) {
      return () => {
        cancelled = true;
      };
    }

    const prefetchNextTreeQuestions = async () => {
      try {
        const nextTree = await queryClient.fetchQuery(
          getQuestionTreeControllerFindByTreeKeyQueryOptions(
            {
              surveyId: parsedSurveyId,
              treeKey: nextTreeKeyForPrefetch,
            },
            {
              query: {
                staleTime: PREFETCH_STALE_TIME_MS,
              },
            },
          ),
        );

        const nextTreeId = nextTree?.id;
        if (cancelled || !nextTreeId) return;

        await queryClient.prefetchQuery(
          getQuestionControllerFindByTreeIdQueryOptions(
            nextTreeId,
            { locale: patientLocaleKey },
            {
              query: {
                staleTime: PREFETCH_STALE_TIME_MS,
              },
            },
          ),
        );
      } catch {
        // Prefetch should not affect normal survey navigation when it fails.
      }
    };

    prefetchNextTreeQuestions();

    return () => {
      cancelled = true;
    };
  }, [nextTreeKeyForPrefetch, patientLocaleKey, queryClient, surveyId]);

  // sx_collect_associated용: 선택된 증상 트리 키 추출
  // V1의 selectedSxTrees 로직 포팅
  // V1: alreadyAddedSymptoms = intersection(answerKeys, reportState.treeStates.map(i => i.treeKey))
  const selectedSymptomTreeKeys = useMemo(() => {
    if (!reportState?.treeStates) return [];

    // 방법 1: treeStates의 treeKey에서 sx- 로 시작하는 것들 추출
    // 증상이 선택되면 해당 증상 트리의 treeState가 생성됨
    const symptomTreeKeys = reportState.treeStates
      .map((ts) => ts.treeKey)
      .filter((key) => key && key.startsWith('sx-'));

    // 방법 2: answerMap에서도 추가로 sx- 키 추출 (sx_select 등에서 선택된 것)
    const answerSymptomKeys: string[] = [];
    reportState.treeStates.forEach((treeState) => {
      if (!treeState.answerMap) return;
      Object.values(treeState.answerMap).forEach((answer: any) => {
        if (answer?.answerKeys && Array.isArray(answer.answerKeys)) {
          answer.answerKeys.forEach((key: string) => {
            if (key && key.startsWith('sx-')) {
              answerSymptomKeys.push(key);
            }
          });
        }
      });
    });

    const allKeys = [...symptomTreeKeys, ...answerSymptomKeys];
    return Array.from(new Set(allKeys)); // 중복 제거
  }, [reportState?.treeStates]);

  return (
    <div className="flex flex-col w-full">
      <PaTreeHeader
        survey={survey as any}
        tree={treeByKey as any}
        logoUrl={org?.logoUrl}
        stepKeys={enabledStepKeys}
        currentTreeKey={treeKey}
        onBack={navigation.goPrevTree}
      />
      <LiveTestingContainer.Provider
        initialState={{
          selectedTreeId: treeId,
          selectedTreeKey: treeKey,
          surveyId,
          organizationId,
          locale: patientLocaleKey,
          visitUuid: visit?.uuid,
          visitSex: reportState?.systemValueMap?.sex as string | undefined,
          stageMarkerStages,
          showHeader: false,
          selectedSymptomTreeKeys,
          onSkipQuestion: navigation.goNextTree,
          reportState,
          survey,
          enabledOnly: true,
        }}
      >
        {/* {(treeTitle || treeDesc) && (
          <div className="flex-col gap-2">
            {treeTitle && <div className="text-lg font-bold text-primary">{treeTitle}</div>}
            {treeDesc && <div className="text-desc">{treeDesc}</div>}
          </div>
        )} */}
        <PaSurveyTreePageBody
          treeTitleKey={treeTitleKey}
          treeDescKey={treeDescKey}
          stageTranslationKeys={stageTranslationKeys}
        />
      </LiveTestingContainer.Provider>
    </div>
  );
}

export default function PaSurveyTreePage() {
  return (
    <SurveyContainer.Provider>
      <PaSurveyTreePageContent />
    </SurveyContainer.Provider>
  );
}
